import type {
  AgentPolicyRecommendation,
  EnforcementHop,
  EnforcementLayer,
  EnforcementStatus,
  EstateAgentDetail,
} from "@/api/temp/AgentEstateService";
import type {
  AgentAccessFlow,
  FlowBranch,
  FlowEnforcementIndicator,
  FlowGovernanceState,
} from "@/api/temp/TrustAIExecutiveService";
import { accessPathsFor } from "./enforcement";
import { BUSINESS_UNIT_LABELS, getRecommendationCatalog } from "./seed";

/**
 * The Intent Policies in effect for one agent, as the hops of each access
 * path. Present only for agents an Intent Policy maps to.
 */
export interface FlowEnforcement {
  policies: Array<{
    policyId: string;
    policyName: string;
    agentStatus: EnforcementStatus;
    /** Keyed by the path id `accessPathsFor` produces. */
    hopsByPath: Record<string, EnforcementHop[]>;
  }>;
}

const indicatorsFor = (
  enforcement: FlowEnforcement,
  pathId: string,
  layer: EnforcementLayer,
): FlowEnforcementIndicator[] =>
  enforcement.policies.flatMap(policy => {
    const hop = policy.hopsByPath[pathId]?.find(item => item.layer === layer);
    return hop
      ? [
          {
            layer,
            status: hop.status,
            control: hop.control,
            policyId: policy.policyId,
            pathId,
          },
        ]
      : [];
  });

/** All views share these missing controls; historical violations stay historical. */
export function buildAgentAccessFlow(
  agent: EstateAgentDetail,
  recommendations: AgentPolicyRecommendation[],
  enforcement?: FlowEnforcement,
): AgentAccessFlow {
  const gaps =
    agent.securityStatus === "NOT_EVALUATED" ? [] : (agent.policyGaps ?? []);
  const branches: FlowBranch[] = agent.mcpServices.flatMap((service): FlowBranch[] => {
    // The server's own technology, not its display name: gaps and policy
    // selectors are keyed by technology, and a catalog name like "SharePoint
    // MCP - Client Onboarding & KYC Documents" is not one.
    const resource = service.technology;
    const resourceGaps = gaps.filter(gap => gap.resource === resource);
    const resourcePolicies = agent.appliedPolicies.filter(policy => {
      const entry = getRecommendationCatalog().find(
        item => item.id === policy.id,
      );
      return entry?.selector.technologies.includes(resource);
    });
    const state: FlowGovernanceState =
      agent.securityStatus === "NOT_EVALUATED"
        ? "NOT_EVALUATED"
        : resourceGaps.length
          ? "POLICY_GAP"
          : resourcePolicies.length
            ? "POLICY_ASSIGNED"
            : "NOT_EVALUATED";
    const detail =
      state === "POLICY_GAP"
        ? resourceGaps.map(gap => gap.title).join("; ")
        : state === "POLICY_ASSIGNED"
          ? resourcePolicies.map(policy => policy.name).join(", ")
          : "This access path has no verified policy evaluation.";
    // An agent an Intent Policy maps to reads one branch per path - server,
    // its tools, the data source - so each hop can carry the control in force
    // there. Twenty per-tool branches would bury those controls.
    if (enforcement)
      return accessPathsFor(agent.id, [service]).map(path => {
        const tools = service.tools.filter(tool =>
          path.toolIds.includes(tool.id),
        );
        const toolGaps = resourceGaps.filter(gap =>
          tools.some(tool => gap.toolNames.includes(tool.name)),
        );
        const [first] = tools;
        return {
          id: path.id,
          accessLayer: { label: service.name, detail: "MCP Server" },
          accessLayers: [
            {
              label: service.name,
              detail: "MCP Server",
              target: { kind: "service" as const, id: service.id },
              state,
              stateDetail: detail,
              gapIds: resourceGaps.map(gap => gap.id),
              enforcement: indicatorsFor(enforcement, path.id, "SERVER"),
            },
            {
              label: `${tools.length} ${tools.length === 1 ? "tool" : "tools"}`,
              detail: "Tools",
              target: { kind: "service" as const, id: service.id },
              gapIds: toolGaps.map(gap => gap.id),
              enforcement: indicatorsFor(enforcement, path.id, "TOOL"),
            },
          ],
          dataPlatform: {
            label: first?.dataSourceName ?? first?.dataAccess ?? service.name,
            detail: first?.dataSourcePlatform ?? resource,
            target: {
              kind: "resource" as const,
              id: first?.dataSourceId ?? first?.dataAccess,
            },
            enforcement: indicatorsFor(enforcement, path.id, "DATA"),
          },
          resourcePath: [first?.dataAccess ?? service.name],
          accessState: state,
          accessStateDetail: detail,
          dataState: state,
          dataStateDetail: detail,
        };
      });
    return service.tools.map(tool => {
      const toolGaps = resourceGaps.filter(gap =>
        gap.toolNames.includes(tool.name),
      );
      return {
        id: `${agent.id}-${tool.id}`,
        accessLayer: { label: service.name, detail: "MCP Server" },
        accessLayers: [
          {
            label: service.name,
            detail: "MCP Server",
            target: { kind: "service", id: service.id },
            state,
            stateDetail: detail,
            gapIds: resourceGaps.map(gap => gap.id),
          },
          {
            label: tool.name,
            detail: "Tool",
            target: { kind: "tool", id: tool.id },
            gapIds: toolGaps.map(gap => gap.id),
            ...(toolGaps.length
              ? {
                  state: "POLICY_GAP" as const,
                  stateDetail: toolGaps.map(gap => gap.title).join("; "),
                }
              : {}),
          },
        ],
        dataPlatform: {
          label: tool.dataAccess,
          detail: tool.dataSourcePlatform ?? resource,
          target: {
            kind: "resource",
            id: tool.dataSourceId ?? tool.dataAccess,
          },
        },
        resourcePath: [tool.dataAccess],
        accessState: state,
        accessStateDetail: detail,
        dataState: state,
        dataStateDetail: detail,
      };
    });
  });
  return {
    id: agent.id,
    name: agent.name,
    identity: agent.identityName,
    identityType: "SERVICE_ACCOUNT",
    identityTarget: { kind: "identity" },
    businessUnit: BUSINESS_UNIT_LABELS[agent.businessUnit],
    enforcementStatus: agent.enforcementReadiness?.status,
    status:
      agent.securityStatus === "NOT_SECURE"
        ? "NEEDS_ATTENTION"
        : agent.securityStatus,
    activePolicies: agent.appliedPolicies.length,
    policyGaps: gaps.length,
    recentViolations: agent.recentActivity.filter(
      event => event.governanceViolation,
    ).length,
    branches,
    agentEnforcement: enforcement?.policies.map(policy => ({
      layer: "AGENT" as const,
      status: policy.agentStatus,
      control: policy.policyName,
      policyId: policy.policyId,
    })),
    evidence: gaps.map(gap => ({
      id: gap.id,
      title: gap.title,
      resource: gap.resource,
      state: "POLICY_GAP",
      detail: gap.description,
      recommendationId: gap.recommendationId,
      recommendedPolicy: recommendations.find(
        item => item.id === gap.recommendationId,
      )?.name,
    })),
  };
}
